import { css, html, LitElement } from 'lit';
import { state, property } from 'lit/decorators.js';
import '@digital-realty/ix-button/ix-button.js';
import '@digital-realty/ix-dialog/ix-dialog.js';
import '@digital-realty/ix-switch/ix-switch.js';
import '@digital-realty/ix-radio/ix-radio.js';
import { TWStyles } from '../styles/tw.js';
import { renderBlurb } from './partials/cookie.js';

export interface ICookieSwitch {
  on: boolean;
  disabled: boolean;
}

export interface ICookieSelectionStatus {
  strictly_necessary?: boolean;
  performance?: boolean;
  targeting?: boolean;
}

export type ICookieSelectionStatusProp =
  | ICookieSelectionStatus
  | string
  | boolean
  | null;

// eslint-disable-next-line no-shadow
export enum EMode {
  All = 'ALL',
  Reject = 'REJECT',
  Some = 'SOME',
}

export class IxCookieDialog extends LitElement {
  static styles = [
    TWStyles,
    css`
      :host {
        --ix-radio-label-line-height: var(--text-default-line-height, 1.75em);
      }
    `,
  ];

  @property({ type: String, attribute: 'policy-url' }) policyUrl: string =
    '/cookie-policy';

  @state() mode: EMode = EMode.All;

  @property({ type: Object }) selection: ICookieSelectionStatusProp = {};

  @property({ type: Boolean }) open: boolean = false;

  @state() strictlySelection: ICookieSwitch = {
    on: true,
    disabled: true,
  };

  @state() performanceSelection: ICookieSwitch = {
    on: true,
    disabled: true,
  };

  @state() targetingSelection: ICookieSwitch = {
    on: true,
    disabled: true,
  };

  @state() customising: boolean = false;

  @state() hideSwitches: boolean = false;

  update(changedProperties: any) {
    super.update(changedProperties);
    if (changedProperties.has('mode')) {
      switch (this.mode) {
        case EMode.All:
          this.acceptAll();
          break;
        case EMode.Reject:
          this.rejectAllExcept();
          break;
        default:
          this.acceptSelected();
      }
    }

    if (changedProperties.has('selection') || changedProperties.has('open')) {
      // if we are not submitting, restore the previous selection
      if (!this.submitting) {
        switch (this.selection) {
          case null:
            this.mode = EMode.All;
            break;
          case true:
            this.mode = EMode.All;
            break;
          case false:
            this.mode = EMode.Reject;
            break;
          default:
            this.mode = EMode.Some;
            this.acceptSelected();
        }
      }
    }
  }

  private async acceptAll() {
    this.hideSwitches = true;
    this.performanceSelection = {
      on: true,
      disabled: false,
    };

    this.targetingSelection = {
      on: true,
      disabled: false,
    };

    await this.updateComplete;
    this.hideSwitches = false;
  }

  private async rejectAllExcept() {
    this.hideSwitches = true;
    this.performanceSelection = {
      on: false,
      disabled: false,
    };

    this.targetingSelection = {
      on: false,
      disabled: false,
    };

    await this.updateComplete;
    this.hideSwitches = false;
  }

  private async acceptSelected() {
    if (!this.customising) {
      this.hideSwitches = true;
      if (this.selection && typeof this.selection === 'object') {
        const { performance, targeting } = this.selection;
        this.performanceSelection = {
          on: performance !== undefined ? performance : true,
          disabled: false,
        };

        this.targetingSelection = {
          on: targeting !== undefined ? targeting : true,
          disabled: false,
        };
      } else {
        this.performanceSelection = {
          on: true,
          disabled: false,
        };

        this.targetingSelection = {
          on: true,
          disabled: false,
        };
      }

      await this.updateComplete;
      this.hideSwitches = false;
    }
  }

  getCookieSelectedStatus(): ICookieSelectionStatus {
    return {
      strictly_necessary: this.strictlySelection.on,
      performance: this.performanceSelection.on,
      targeting: this.targetingSelection.on,
    };
  }

  formCookieSelection: null | ICookieSelectionStatus = null;

  submitting = false;

  private renderSwitch(
    label: string,
    name: string,
    disabled: boolean,
    selected: boolean,
  ) {
    return html`
      <label
        class="flex items-center"
        data-testid="cookie-dialog-${name}-switch"
      >
        <div class="grow">${label}</div>
        ${this.hideSwitches
          ? html`<ix-switch class="flex"> </ix-switch>`
          : html`<ix-switch
              class="flex"
              ?disabled=${disabled}
              ?selected=${selected}
              name=${name!}
              @change=${() => {
                this.customising = true;
                if (this.mode !== EMode.Some) {
                  this.mode = EMode.Some;
                }
              }}
            >
            </ix-switch>`}
      </label>
    `;
  }

  render() {
    return html`<ix-dialog
      prevent-cancel
      ?open=${this.open}
      @close=${() => {
        if (this.submitting) {
          switch (this.mode) {
            case EMode.All:
              this.dispatchEvent(new CustomEvent('accept-all'));
              break;
            case EMode.Reject:
              this.dispatchEvent(new CustomEvent('reject-all'));
              break;
            default:
              this.dispatchEvent(
                new CustomEvent('accept-some', {
                  detail: this.formCookieSelection,
                }),
              );
          }
        }
      }}
      @closed=${() => {
        this.dispatchEvent(new CustomEvent('closed'));
      }}
      preventCloseOnScrimClick
    >
      <form
        id="customCookieForm"
        slot="content"
        method="dialog"
        @submit=${(e: HTMLFormElement) => {
          const formData = new FormData(e.target as unknown as HTMLFormElement);
          if (formData.get('cookie-radio') === 'customselect') {
            this.formCookieSelection = {
              strictly_necessary: true,
              performance: formData.get('performance') !== null,
              targeting: formData.get('targeting') !== null,
            };
          } else {
            this.formCookieSelection = this.getCookieSelectedStatus();
          }
          this.submitting = true;
          this.open = false;
        }}
        class="flex flex-col gap-6 max-w-[486px]"
      >
        <div>
          <h1 class="dlr-text-heading mb-6">Cookie Settings</h1>
          ${renderBlurb(this.policyUrl)}
        </div>
        <div class="flex flex-col gap-2">
          <ix-radio
            @change=${() => {
              this.customising = false;
              this.mode = EMode.All;
            }}
            data-testid="cookie-dialog-all-radio"
            name="cookie-radio"
            label="Accept All"
            value="preselect"
            ?checked=${this.mode === EMode.All}
            checked
          ></ix-radio>
          <ix-radio
            data-testid="cookie-dialog-strictly-radio"
            @change=${() => {
              this.customising = false;
              this.mode = EMode.Reject;
            }}
            name="cookie-radio"
            label="Reject all except for Strictly Necessary"
            value="preselect"
            ?checked=${this.mode === EMode.Reject}
          ></ix-radio>
          <ix-radio
            data-testid="cookie-dialog-some-radio"
            @change=${() => {
              this.customising = false;
              this.mode = EMode.Some;
            }}
            name="cookie-radio"
            label="Accept Selected"
            value="customselect"
            ?checked=${this.mode === EMode.Some}
          ></ix-radio>
        </div>
        <div class="flex flex-col gap-4">
          ${this.renderSwitch(
            'Strictly Necessary Cookies',
            'strictly',
            this.strictlySelection.disabled,
            this.strictlySelection.on,
          )}
          ${this.renderSwitch(
            'Performance Cookies',
            'performance',
            this.performanceSelection.disabled,
            this.performanceSelection.on,
          )}
          ${this.renderSwitch(
            'Targeting Cookies',
            'targeting',
            this.targetingSelection.disabled,
            this.targetingSelection.on,
          )}
        </div>
      </form>
      <div slot="actions" class="flex gap-2 flex-wrap">
        <ix-button
          appearance="text"
          @click=${() => {
            this.open = false;
          }}
          data-testid="cookie-dialog-dismiss"
        >
          <span class="button-label">Dismiss</span>
        </ix-button>
        <ix-button
          appearance="text"
          type="submit"
          form="customCookieForm"
          data-testid="cookie-dialog-submit"
        >
          <span class="button-label">Save cookie settings</span>
        </ix-button>
      </div>
    </ix-dialog>`;
  }
}

window.customElements.define('ix-cookie-dialog', IxCookieDialog);
